// The question, asked once, in plain words.
//
// **What is being asked for here is a recording of somebody's game, not
// "analytics".** A command log replays to exactly one sequence of states, so
// a submitted game can be watched back move by move — and a screen that said
// "help us improve Factory Chess by sharing anonymous usage data" would be
// describing something smaller than what is actually sent. So the copy names
// the thing: every move, and it can be replayed. If that reads as a lot to
// ask, that is the disclosure working.
//
// Three rules it follows, each of which rules something out:
//
//   - **Neither button is preselected as "yes".** Decline takes the autofocus,
//     which is the same rule the run-discard dialog follows: the safe choice
//     is the default one, and here safe means "nothing leaves the machine".
//   - **Dismissing is not agreeing.** There is no close button and Escape
//     declines, because a modal that sends your games when you press Escape
//     has taken silence for consent.
//   - **It says how to change your mind, on the screen where the choice is
//     made.** A setting somebody has to go looking for is a setting they do
//     not know they have.
//
// Never `window.confirm`: it is the browser's chrome, it blocks the main
// thread so the board freezes behind it, and Playwright cannot reach one.

import {
  Btn,
  Modal,
  ModalActions,
  ModalBody,
  ModalTitle,
  styled,
  Text
} from "./kit";

interface Props {
  open: boolean;
  onAnswer(agreed: boolean): void;
}

// **Full ink, not the dim, in both.** This is a disclosure somebody is deciding
// on; a consent screen that recedes is one that was not really read.

/** The "what is sent" lists. A list, because prose hides items. */
const List = styled("ul")(({ theme }) => ({
  margin: `0 0 ${theme.kit.space.md}px`,
  paddingLeft: theme.kit.space.lg,
  color: theme.kit.color.ink,
  fontSize: 14,
  lineHeight: 1.55,
  "& li": { marginBottom: theme.kit.space.xs },
  "& strong": { color: theme.kit.color.cream }
}));

/** A paragraph of the disclosure. */
const Para = styled("p")(({ theme }) => ({
  margin: `0 0 ${theme.kit.space.md}px`,
  color: theme.kit.color.ink,
  fontSize: 14,
  lineHeight: 1.55,
  "& strong": { color: theme.kit.color.cream }
}));

/** The two halves of the disclosure. Heading voice — each names a thing. */
const Subhead = styled("h3")(({ theme }) => ({
  margin: `0 0 ${theme.kit.space.xs}px`,
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 17,
  color: theme.kit.color.cream
}));

export function ConsentDialog({ open, onAnswer }: Props) {
  return (
    <Modal
      open={open}
      // **Escape declines rather than closing.** `onClose` fires for Escape and
      // for a backdrop click, and both are a player saying "not this" — the
      // one thing they must not do is leave the question open to be asked
      // again next game, which is what a plain dismiss would do.
      onClose={() => onAnswer(false)}
      aria-labelledby="consent-title"
      maxWidth="sm"
    >
      <ModalTitle id="consent-title">May we watch your games?</ModalTitle>
      <ModalBody>
        <Para>
          This is a prototype, and the most useful thing we can look at is a
          game somebody actually played. If you agree, then{" "}
          <strong>when a game finishes</strong> we send ourselves a copy of it.
        </Para>

        <Subhead>What that means</Subhead>
        <List>
          <li>
            <strong>Every move of the game</strong>, in order — enough to replay
            it and watch it back. Not a summary.
          </li>
          <li>
            A count of what you used: how much you mined, how many belts you
            laid, what you built.
          </li>
          <li>
            A random id for this browser, so we can tell one player&apos;s games
            from another&apos;s. It is not your name and is not linked to
            anything.
          </li>
          <li>Which build you were on, so the game can be replayed correctly.</li>
        </List>

        <Subhead>What we do not send</Subhead>
        <List>
          <li>Your name, your display name, or anything you type.</li>
          <li>
            The room code — that belongs to whoever you are playing as much as
            to you.
          </li>
          <li>
            <strong>No game of yours at all</strong> until it finishes, and none
            if you say no.
          </li>
        </List>

        {/*
          **The bullet above used to read "anything at all", and that stopped
          being true.** The site now loads Google Analytics on every visit,
          which no answer on this screen turns off — so the old wording was the
          product promising something it had quietly stopped doing, which is
          worse than not having said it. What this dialog controls is the game
          recording, and it now says exactly that and nothing wider.

          Here rather than in the list, because it is not a thing this screen
          decides: putting it among the bullets would file it under a question
          the buttons below answer.
        */}
        <Para>
          Separately, and whatever you choose here, the site counts anonymous
          play statistics: page views, how far a run gets, and — when one ends —
          totals like how many moves it took, how much ore you dug and how much
          track you laid. Those are <strong>counts, not a recording</strong>:
          they say how a run went, and nothing in them can reconstruct it. What
          this screen decides is the recording.
        </Para>

        <Text tone="dim" size={13} block>
          You can change your mind whenever you like, under the gear on the
          menu. Saying no now costs you nothing — every part of the game works
          either way.
        </Text>
      </ModalBody>
      <ModalActions>
        {/* The safe choice takes the focus, so Enter declines. */}
        <Btn
          autoFocus
          variant="ghost"
          data-testid="consent-decline"
          onClick={() => onAnswer(false)}
        >
          No thanks
        </Btn>
        <Btn
          variant="primary"
          data-testid="consent-agree"
          onClick={() => onAnswer(true)}
        >
          Yes, send my games
        </Btn>
      </ModalActions>
    </Modal>
  );
}
